import { CreateMonitorDTO, MonitorDTO } from '@meshwatch/types';
import {
  CreateTableOutput,
  DeleteItemOutput,
  DocumentClient,
  TransactWriteItemList,
  PutItemInputAttributeMap,
} from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';
import { get } from 'lodash';
import uuid from 'uuid';

import { DEFAULT_DYNAMO_DB_CLIENT, DynamoDBClient } from '../../shared/aws/dynamodb';
import { NotFoundException } from '../../shared/rest';
import { completeGettingStartedTaskUpdate } from '../../user/db/model';
import DateUtils from '../../util/date';
import DynamoMonitoringDatasource from '../db/datasource';
import { MONITORING_TABLE_SCHEDULER_GSI } from '../db/schema';
import { UserDatasource, DynamoUserDatasource } from '../../user';

import { MonitorDynamoItem, MonitorSortKey } from './types';

export interface MonitorDatasource {
  createTable: () => Promise<PromiseResult<CreateTableOutput, AWSError>>;
  createMonitor: (monitor: CreateMonitorDTO) => Promise<MonitorDTO>;
  updateMonitor: (monitor: MonitorDTO) => Promise<MonitorDTO>;
  deleteMonitor: (
    userId: string,
    monitorId: string
  ) => Promise<PromiseResult<DeleteItemOutput, AWSError>>;
  getMonitor: (userId: string, monitorId: string) => Promise<MonitorDTO>;
  searchMonitors: (userId: string) => Promise<MonitorDTO[]>;
  bookmarkMonitor: (userId: string, monitorId: string, bookmarked: boolean) => Promise<MonitorDTO>;
  getMonitorsByScheduler: (schedulerName: string) => Promise<MonitorDTO[]>;
}

export class DynamoMonitorDatasource extends DynamoMonitoringDatasource
  implements MonitorDatasource {
  private readonly userDatasource: UserDatasource;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super(dynamoClient);
    this.userDatasource = new DynamoUserDatasource(dynamoClient);
  }

  public bookmarkMonitor = (userId: string, monitorId: string, bookmarked: boolean) => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    const sortKey = this.encodeSortKey({ monitorId });

    return this.dynamoClient
      .updateItem({
        Key: {
          hashKey,
          sortKey,
        },
        UpdateExpression: `set isBookmarked = :isBookmarked`,
        ExpressionAttributeValues: {
          ':isBookmarked': bookmarked,
        },
        ReturnValues: 'ALL_NEW',
      })
      .then(resp => this.monitorFromDynamoItem(resp.Attributes as MonitorDynamoItem));
  };

  public getMonitor = (userId: string, monitorId: string) => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    const sortKey = this.encodeSortKey({ monitorId });

    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `hashKey = :hashKey and sortKey = :sortKey`,
        ExpressionAttributeValues: {
          ':hashKey': hashKey,
          ':sortKey': sortKey,
        },
      })
      .then(resp => {
        if (!resp.Items || resp.Items.length === 0) {
          throw new NotFoundException(`Could not find monitor id = ${monitorId}`);
        }
        return this.monitorFromDynamoItem(resp.Items[0] as MonitorDynamoItem);
      });
  };

  public deleteMonitor = (userId: string, monitorId: string) => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    const sortKey = this.encodeSortKey({ monitorId });

    return this.dynamoClient.deleteItem({
      Key: {
        hashKey,
        sortKey,
      },
    });
  };

  public searchMonitors = (userId: string) => {
    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `hashKey = :hashKey and begins_with(sortKey, :sortKey)`,
        ExpressionAttributeValues: {
          ':hashKey': DynamoMonitoringDatasource.encodeHashKey({ userId }),
          ':sortKey': 'monitor',
        },
      })
      .then(this.mapDynamoRows);
  };

  public getMonitorsByScheduler = (scheduler: string) => {
    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `scheduler = :scheduler and begins_with(sortKey, :sortKey)`,
        IndexName: MONITORING_TABLE_SCHEDULER_GSI,
        ExpressionAttributeValues: {
          ':scheduler': scheduler,
          ':sortKey': 'monitor',
        },
      })
      .then(this.mapDynamoRows);
  };

  public createMonitor = async (payload: CreateMonitorDTO) => {
    const taskName = 'createMonitor';
    const monitor: MonitorDTO = { ...payload, id: uuid.v4(), created: new Date() };
    const Item = this.monitorToDynamoItem(monitor) as PutItemInputAttributeMap;
    const TransactItems: TransactWriteItemList = [{ Put: { Item, TableName: this.tableName } }];

    const userInfo = await this.userDatasource.getUserInfo(monitor.userId);
    if (!userInfo.gettingStarted.completedTasks[taskName]) {
      const completeTaskUpdateItemInput = completeGettingStartedTaskUpdate(
        monitor.userId,
        monitor.created,
        taskName
      );
      TransactItems.push({ Update: completeTaskUpdateItemInput });
    }

    return this.dynamoRawClient.transactWriteItems({ TransactItems }).then(_ => monitor);
  };

  public updateMonitor = (updateMonitorPayload: MonitorDTO) => {
    return this._putMonitor(updateMonitorPayload);
  };

  public _putMonitor = (monitor: MonitorDTO): Promise<MonitorDTO> => {
    const Item = this.monitorToDynamoItem(monitor);
    return this.dynamoClient.putDocumentToDynamo({ Item }).then(_ => monitor);
  };

  private monitorToDynamoItem = (monitor: MonitorDTO): MonitorDynamoItem => {
    return {
      hashKey: DynamoMonitorDatasource.encodeHashKey(monitor),
      sortKey: this.encodeSortKey({ monitorId: monitor.id }),
      name: monitor.name,
      body: get(monitor, 'body'),
      scheduler: monitor.scheduler,
      apdex: get(monitor, 'apdex'),
      endpoint: monitor.endpoint,
      headers: get(monitor, 'headers'),
      isBookmarked: monitor.isBookmarked,
      location: monitor.location,
      regions: get(monitor, 'regions'),
      type: monitor.type,
      created: DateUtils.getUnixTimestamp(monitor.created),
    };
  };

  private mapDynamoRows = (
    resp: PromiseResult<DocumentClient.QueryOutput, AWSError>
  ): MonitorDTO[] => {
    const rows = resp.Items as MonitorDynamoItem[];
    return rows.map(this.monitorFromDynamoItem);
  };

  /* sortKey */
  private encodeSortKey({ monitorId }: MonitorSortKey): string {
    return `monitor#${monitorId}`;
  }
}

const dynamoMonitorDatasource = new DynamoMonitorDatasource();

export default dynamoMonitorDatasource;
